/**
 * Map MediaPipe normalized landmarks (0–1 in video frame space) onto a canvas
 * that mirrors how CSS `object-fit` paints the <video> element.
 */

export type ObjectFitMode = "cover" | "contain";

export interface VideoLayout {
  /** Drawn video width in canvas pixels */
  drawWidth: number;
  /** Drawn video height in canvas pixels */
  drawHeight: number;
  /** Left offset of the video content inside the canvas */
  offsetX: number;
  /** Top offset of the video content inside the canvas */
  offsetY: number;
  canvasWidth: number;
  canvasHeight: number;
  videoWidth: number;
  videoHeight: number;
  fit: ObjectFitMode;
  /** Horizontal mirror (selfie / front camera). Flips landmark x to match CSS scaleX(-1). */
  mirrorX: boolean;
}

/**
 * Compute where the video content sits inside a canvas of the same CSS box,
 * matching `object-fit: cover | contain` (no object-position override = center).
 */
export function computeVideoLayout(
  canvasWidth: number,
  canvasHeight: number,
  videoWidth: number,
  videoHeight: number,
  fit: ObjectFitMode = "contain",
  mirrorX = false,
): VideoLayout {
  const cw = Math.max(1, canvasWidth);
  const ch = Math.max(1, canvasHeight);
  const vw = Math.max(1, videoWidth);
  const vh = Math.max(1, videoHeight);

  const scale =
    fit === "cover"
      ? Math.max(cw / vw, ch / vh)
      : Math.min(cw / vw, ch / vh);

  const drawWidth = vw * scale;
  const drawHeight = vh * scale;
  const offsetX = (cw - drawWidth) / 2;
  const offsetY = (ch - drawHeight) / 2;

  return {
    drawWidth,
    drawHeight,
    offsetX,
    offsetY,
    canvasWidth: cw,
    canvasHeight: ch,
    videoWidth: vw,
    videoHeight: vh,
    fit,
    mirrorX,
  };
}

/** Map a normalized landmark (x,y in 0–1 of the video frame) to canvas pixels. */
export function landmarkToCanvas(
  x: number,
  y: number,
  layout: VideoLayout,
): { x: number; y: number } {
  const nx = layout.mirrorX ? 1 - x : x;
  return {
    x: layout.offsetX + nx * layout.drawWidth,
    y: layout.offsetY + y * layout.drawHeight,
  };
}
